'use client';

import React from 'react';

const layers = [
  {
    n: '03',
    title: 'Agentic AI',
    subtitle: 'Do the work',
    body:
      'Autonomous multi-step workflows — Leasing Performer, Maintenance dispatcher, Month-end Close, Collections.',
    color: '#7C3AED',
    glow: 'rgba(124,58,237,0.25)',
  },
  {
    n: '02',
    title: 'Generative AI',
    subtitle: 'Help me work',
    body:
      'Message drafting, report generation, Ask Propmatic, auto-coded invoices, lease abstraction — LLMs on tap.',
    color: '#A78BFA',
    glow: 'rgba(167,139,250,0.22)',
  },
  {
    n: '01',
    title: 'Predictive / Analytical AI',
    subtitle: "Tell me what's coming",
    body:
      'ML models on structured data — churn risk, dynamic rent pricing, vacancy forecasts, anomaly detection.',
    color: '#22C55E',
    glow: 'rgba(34,197,94,0.2)',
  },
];

const types = [
  { icon: '🧠', label: 'Machine Learning', sub: 'Predictions from structured data' },
  { icon: '📝', label: 'NLP', sub: 'Leases, invoices, emails, tickets' },
  { icon: '👁', label: 'Computer Vision', sub: 'Photos, plans, damage, documents' },
  { icon: '✨', label: 'Generative AI', sub: 'Drafts, summaries, answers' },
  { icon: '🤖', label: 'Agentic AI', sub: 'Multi-step autonomous workflows' },
];

export default function AILayerStack() {
  return (
    <section id="architecture" className="py-24 px-6 relative">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 50% 60% at 50% 50%, rgba(124,58,237,0.08) 0%, transparent 70%)',
        }}
      />

      <div className="relative max-w-6xl mx-auto">
        <div className="reveal-blur mb-12 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[rgba(124,58,237,0.25)] mb-5">
            <span className="text-[10px] font-mono text-[#A78BFA] tracking-widest uppercase">
              Architecture
            </span>
          </div>
          <h2
            className="font-manrope font-extrabold tracking-tighter text-[#EEEEF0]"
            style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)' }}
          >
            Three layers. One stack.
          </h2>
          <p className="mt-4 text-[#9999A8] max-w-2xl mx-auto">
            Every feature on this page fits cleanly into one of these three layers. Nothing ad-hoc.
          </p>
        </div>

        {/* Stacked layers visual */}
        <div className="flex flex-col gap-3 stagger-children">
          {layers.map((l) => (
            <div
              key={l.n}
              className="reveal-blur bento-card card-shine relative p-6 md:p-8"
              style={{
                borderColor: l.glow,
                background: `linear-gradient(135deg, ${l.glow} 0%, #1C1C24 70%)`,
              }}
            >
              <div className="flex items-start gap-6">
                <div
                  className="font-manrope font-extrabold text-4xl md:text-5xl tracking-tighter shrink-0"
                  style={{ color: l.color }}
                >
                  {l.n}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <h3 className="font-manrope font-bold text-xl md:text-2xl text-[#EEEEF0]">
                      {l.title}
                    </h3>
                    <span
                      className="text-[10px] font-mono tracking-widest uppercase"
                      style={{ color: l.color }}
                    >
                      {l.subtitle}
                    </span>
                  </div>
                  <p className="text-sm md:text-base text-[#9999A8] mt-2 leading-relaxed">
                    {l.body}
                  </p>
                </div>
              </div>
              <div className="scan-line" />
            </div>
          ))}
        </div>

        {/* AI types row */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-5 gap-3 stagger-children">
          {types.map((t) => (
            <div key={t.label} className="reveal-blur bento-card p-4 text-center">
              <div className="text-2xl mb-2">{t.icon}</div>
              <div className="font-manrope font-semibold text-sm text-[#EEEEF0]">{t.label}</div>
              <div className="text-[10px] font-mono text-[#5A5A6E] mt-1 leading-snug">{t.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
